import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, type RefObject } from 'react'

// ── Moving a table's columns by dragging their headings ────────────────
//
// A press on a heading that travels sideways becomes a drag of its column; one that doesn't stays a
// click, so the heading's sort button keeps working. The column lands in the gap between the two
// headings whose midpoints the pointer is between. Only the headings that move count — they carry
// data-col — so a fixed cell in the row, like the drill-down's disclosure column, is no column to land
// beside, and never shifts a drop. Escape abandons the drag — only the drag, not the dialog around it.
//
// Pointer events rather than HTML drag and drop: Firefox won't start a native drag from a <button>,
// and every heading is one. Mouse and pen only — on a touchscreen a sideways swipe scrolls the table,
// so the drill-down's Arrange strip moves the columns there, and from the keyboard.

/** How far a press must travel sideways before it is a drag rather than a click. */
export const COLUMN_DRAG_THRESHOLD_PX = 5

export interface ColumnDrag {
    /** The column being dragged. */
    from: number
    /** The gap it would land in — 0 before the first column, n after the last — or null where it already is. */
    slot: number | null
}

export interface ColumnDragHandlers {
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void
    onClickCapture: (e: ReactMouseEvent<HTMLElement>) => void
}

/**
 * @param headingRow The row of headings; where its cells with a data-col sit decides the drop. Index
 * the columns the same way: `from` and `to` count only those cells.
 * @param onMove Called on a drop that changes the order, with the column's old and new index.
 * @returns The drag in progress, if any, and the handlers each heading needs.
 */
export function useColumnDrag(
    headingRow: RefObject<HTMLTableRowElement | null>,
    onMove: (from: number, to: number) => void,
): { drag: ColumnDrag | null; headingProps: (index: number) => ColumnDragHandlers } {
    const [drag, setDrag] = useState<ColumnDrag | null>(null)
    const onMoveRef = useRef(onMove)
    const endPress = useRef<(() => void) | null>(null)
    const swallowClick = useRef(false)

    useEffect(() => { onMoveRef.current = onMove })
    // A dialog closed mid-drag leaves no listeners behind.
    useEffect(() => () => endPress.current?.(), [])

    const press = (from: number, e: ReactPointerEvent<HTMLElement>) => {
        if (e.button !== 0 || e.pointerType === 'touch') return
        endPress.current?.()
        const { pointerId, clientX: startX } = e
        let phase: 'pressed' | 'dragging' | 'abandoned' = 'pressed'
        let slot: number | null = null

        const slotAt = (x: number): number | null => {
            // Only the headings that move are counted: a cell in the row without a data-col (the
            // drill-down's disclosure column) stays where it is, and makes no gap of its own.
            const cells = headingRow.current ? Array.from(headingRow.current.cells).filter(cell => cell.hasAttribute('data-col')) : []
            const gap = cells.filter(cell => {
                const r = cell.getBoundingClientRect()
                return x > r.left + r.width / 2
            }).length
            return gap === from || gap === from + 1 ? null : gap
        }

        const show = (next: number | null) => {
            slot = next
            setDrag({ from, slot: next })
        }

        const onPointerMove = (ev: PointerEvent) => {
            if (ev.pointerId !== pointerId || phase === 'abandoned') return
            if (phase === 'pressed') {
                if (Math.abs(ev.clientX - startX) < COLUMN_DRAG_THRESHOLD_PX) return
                phase = 'dragging'
                window.getSelection?.()?.removeAllRanges()
                show(slotAt(ev.clientX))
                return
            }
            const next = slotAt(ev.clientX)
            if (next !== slot) show(next)
        }

        const onPointerUp = (ev: PointerEvent) => {
            if (ev.pointerId !== pointerId) return
            const wasDrag = phase !== 'pressed'
            const landed = phase === 'dragging' ? slot : null
            end()
            if (!wasDrag) return
            // The release also clicks whatever holds both ends of the drag — often the heading's own
            // sort button. That click is the drag's, not a sort.
            swallowClick.current = true
            window.setTimeout(() => { swallowClick.current = false }, 0)
            if (landed !== null) onMoveRef.current(from, landed > from ? landed - 1 : landed)
        }

        const onPointerCancel = (ev: PointerEvent) => {
            if (ev.pointerId === pointerId) end()
        }

        const onKeyDown = (ev: KeyboardEvent) => {
            if (ev.key !== 'Escape' || phase !== 'dragging') return
            // Caught on its way down, before the dialog's own Escape — which closes it — can see it.
            ev.preventDefault()
            ev.stopPropagation()
            phase = 'abandoned'
            setDrag(null)
        }

        function end() {
            window.removeEventListener('pointermove', onPointerMove)
            window.removeEventListener('pointerup', onPointerUp)
            window.removeEventListener('pointercancel', onPointerCancel)
            window.removeEventListener('keydown', onKeyDown, true)
            endPress.current = null
            setDrag(null)
        }

        window.addEventListener('pointermove', onPointerMove)
        window.addEventListener('pointerup', onPointerUp)
        window.addEventListener('pointercancel', onPointerCancel)
        window.addEventListener('keydown', onKeyDown, true)
        endPress.current = end
    }

    const onClickCapture = (e: ReactMouseEvent<HTMLElement>) => {
        if (!swallowClick.current) return
        swallowClick.current = false
        e.preventDefault()
        e.stopPropagation()
    }

    return {
        drag,
        headingProps: index => ({ onPointerDown: e => press(index, e), onClickCapture }),
    }
}
